import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isGuestActor } from "../helpers/guestMode";
import {
  CONNECTIVITY_CHANGED_EVENT,
  CONNECTIVITY_STATE,
  isOffline,
} from "../helpers/connectivity";
import {
  loadMembershipForAuthUser,
  persistStoredMembershipOrgId,
} from "../helpers/membershipHelpers";

const MembershipContext = createContext(null);

/**
 * Gate 3 — resolves Authenticated User → Membership → (feeds OrganizationContext).
 * Legacy users-row actor remains the permission surface; membership enriches it in App.js.
 */
export function MembershipProvider({ actor, children }) {
  const [profile, setProfile] = useState(null);
  const [membership, setMembership] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshMembership = useCallback(async () => {
    if (!actor || isGuestActor(actor) || !actor.supabase_user_id) {
      setProfile(null);
      setMembership(null);
      setMemberships([]);
      return;
    }

    if (isOffline()) {
      if (actor.membership_id) {
        setMembership({
          id: actor.membership_id,
          organization_id: actor.organization_id,
          role: actor.membership_role,
          employee_name: actor.employee_name,
          profile_id: actor.profile_id,
        });
        setProfile(
          actor.profile_id
            ? {
                id: actor.profile_id,
                supabase_user_id: actor.supabase_user_id,
                is_system_owner: actor.is_system_owner === true,
              }
            : null,
        );
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await loadMembershipForAuthUser(actor.supabase_user_id);
      setProfile(result.profile);
      setMemberships(result.memberships);
      setMembership(result.membership);
    } catch (err) {
      console.warn("[MembershipProvider] load failed — legacy actor path", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [actor]);

  useEffect(() => {
    refreshMembership();
  }, [refreshMembership]);

  useEffect(() => {
    const handleConnectivityRestored = (event) => {
      if (event.detail?.state === CONNECTIVITY_STATE.GOOD) {
        refreshMembership();
      }
    };

    window.addEventListener(CONNECTIVITY_CHANGED_EVENT, handleConnectivityRestored);
    return () => {
      window.removeEventListener(
        CONNECTIVITY_CHANGED_EVENT,
        handleConnectivityRestored,
      );
    };
  }, [refreshMembership]);

  const setCurrentOrganizationId = useCallback(
    (organizationId) => {
      if (!organizationId) {
        return;
      }
      persistStoredMembershipOrgId(organizationId);
      const next = memberships.find((row) => row.organization_id === organizationId);
      if (next) {
        setMembership(next);
      }
    },
    [memberships],
  );

  const value = useMemo(
    () => ({
      profile,
      membership,
      memberships,
      loading,
      error,
      refreshMembership,
      setCurrentOrganizationId,
      organizationId: membership?.organization_id ?? actor?.organization_id ?? null,
      isOwner: membership?.role === "owner",
      isOrgAdmin:
        membership?.role === "owner" || membership?.role === "admin",
      isManager:
        membership?.role === "manager" ||
        membership?.role === "admin" ||
        membership?.role === "owner",
      isEmployee: membership?.role === "employee",
      isSystemOwner: profile?.is_system_owner === true || actor?.is_system_owner === true,
    }),
    [profile, membership, memberships, loading, error, refreshMembership, setCurrentOrganizationId, actor],
  );

  return (
    <MembershipContext.Provider value={value}>
      {children}
    </MembershipContext.Provider>
  );
}

export function useMembership() {
  const context = useContext(MembershipContext);
  if (!context) {
    throw new Error("useMembership must be used within MembershipProvider");
  }
  return context;
}

export function useMembershipOptional() {
  return useContext(MembershipContext);
}
